/**
 * Per-shape type label resolution, shared by every admin surface that names a
 * content item BY ITS KIND rather than only by its title — the Arkisto drawer's
 * item meta and the Julkaistut unit rows (which name both halves of a unit on
 * one row).
 *
 * Lifted out of `ArchiveDrawer.tsx`, where the map was module-private: a second
 * consumer would otherwise have duplicated the shape→key table, and a shape
 * added on one side only would then label correctly in one surface and not the
 * other.
 */

import type { useTranslate } from '../../i18n/useTranslate.js';
import type { AdminContentShape } from './hooks/useAdminContent.js';

// Keyed by the omaplus shape alphabet (`webinar_recording` is retired per D-110
// and absent from the alphabet).
const SHAPE_TYPE_KEYS = {
  article: 'cms.archive.type.article',
  home_card: 'cms.archive.type.homeCard',
  video_listing: 'cms.archive.type.videoListing',
  benefit: 'cms.archive.type.benefit',
  sidebar_link: 'cms.archive.type.sidebarLink',
  banner: 'cms.archive.type.banner',
} as const satisfies Record<AdminContentShape, string>;

/**
 * Resolve a shape's type label. The record is type-exhaustive TODAY, but an
 * older web bundle meeting a newer API that has learned a new shape reaches
 * here with a literal outside the compile-time alphabet — the own-property
 * guard (not a bare index, whose `undefined` would flow into `t()`) falls back
 * to the raw shape literal, surfacing loudly instead of rendering a missing-key
 * artefact.
 */
export function shapeTypeLabel(
  t: ReturnType<typeof useTranslate>,
  shape: AdminContentShape,
): string {
  return Object.hasOwn(SHAPE_TYPE_KEYS, shape) ? t(SHAPE_TYPE_KEYS[shape]) : shape;
}
